import {
  ContactSlugEnum,
  CountryEnum,
  Tooltip,
  getContactFieldTitle,
  getSingleCountry,
  setDateWithTimezone,
  toISOStringWithoutTimezone,
} from '@systemeio/ui'
import debounce from 'lodash.debounce'
import { ContactId } from 'modules/contacts/types/contact-interface'
import React, { useCallback, useEffect } from 'react'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { usePhoneInput } from 'shared/react-international-phone'
import { dateFormat } from 'shared/utils/date-time-format'
import { CONTACT_FIELDS_SORT_ORDER } from '../hooks/use-contact-fields'
import {
  ContactTooltipDataInterface,
  useContactsTooltipData,
} from '../hooks/use-contacts-tooltip-data'

interface ContactInfoTooltipProps {
  children?: JSX.Element | string
  contactId?: ContactId
  removedAt: string | null
}

interface LabelTooltipProps {
  contactData: ContactTooltipDataInterface
  removedAt: string | null
}

const LabelTooltip = ({ contactData, removedAt }: LabelTooltipProps) => {
  const { t } = useLocoTranslation()
  const { user } = useUser()

  const phoneNumber = contactData?.fieldValues.find(
    el => el.contactField.slug === ContactSlugEnum.PhoneNumber,
  )?.value
  const { phone: formattedPhoneNumber } = usePhoneInput({ value: phoneNumber })

  const getSlugValue = (slug: ContactSlugEnum, value: string) => {
    switch (slug) {
      case ContactSlugEnum.Country:
        return Object.values(CountryEnum).includes(value as CountryEnum)
          ? getSingleCountry(value as CountryEnum, user?.dashboardLocale)
          : value
      case ContactSlugEnum.PhoneNumber: {
        return formattedPhoneNumber
      }
      default: {
        return value
      }
    }
  }

  const sortedFields = [...contactData.fieldValues].sort((a, b) => {
    const indexA = CONTACT_FIELDS_SORT_ORDER.indexOf(a.contactField.slug as ContactSlugEnum)
    const indexB = CONTACT_FIELDS_SORT_ORDER.indexOf(b.contactField.slug as ContactSlugEnum)

    if (indexA === -1) return 1
    if (indexB === -1) return -1

    return indexA - indexB
  })

  return (
    <div className="flex flex-col items-start gap-2 text-start">
      {sortedFields.map(el => (
        <p key={el.contactField.slug}>
          <span className="text-text-primary font-bold">
            {getContactFieldTitle(el.contactField)}:{' '}
          </span>
          <span>{getSlugValue(el.contactField.slug as ContactSlugEnum, el.value)}</span>
        </p>
      ))}
      <p>
        <span className="text-text-primary font-bold">{t('dashboard.user.affiliate')}: </span>
        <span>{contactData.referredBy?.email ?? t('global.none')}</span>
      </p>
      {removedAt && (
        <p>
          <span className="text-text-primary font-bold">{t('global.contact_removed')}: </span>
          <span>
            {dateFormat(removedAt, { dateFormat: user?.dateFormat, timeZone: user?.timezone })}
          </span>
        </p>
      )}
    </div>
  )
}

const LabelTooltipSkeleton = () => (
  <div className={'flex flex-col gap-2'}>
    <div className={'bg-skeleton-primary w-[250px] animate-pulse rounded-md text-transparent'}>
      .
    </div>
    <div className={'bg-skeleton-primary w-[250px] animate-pulse rounded-md text-transparent'}>
      .
    </div>
    <div className={'bg-skeleton-primary w-[250px] animate-pulse rounded-md text-transparent'}>
      .
    </div>
    <div className={'bg-skeleton-primary w-[250px] animate-pulse rounded-md text-transparent'}>
      .
    </div>
  </div>
)

const LazyContactInfoTooltip = ({ children, contactId, removedAt }: ContactInfoTooltipProps) => {
  const { contactData, fetchContactData, clearContactData } = useContactsTooltipData()

  const fetchContactDataWithDebounce = useCallback(
    debounce((id: ContactId) => {
      fetchContactData(id)
    }, 500),
    [fetchContactData],
  )

  useEffect(() => {
    return () => {
      fetchContactDataWithDebounce.cancel()
    }
  }, [fetchContactDataWithDebounce])

  const handleMouseEnter = () => {
    if (contactId) {
      fetchContactDataWithDebounce(contactId)
    }
  }

  const handleMouseLeave = () => {
    fetchContactDataWithDebounce.cancel()
    clearContactData()
  }

  const removedAtDateString =
    removedAt &&
    toISOStringWithoutTimezone(setDateWithTimezone(removedAt, 'UTC')).split('T')[0] + 'T'

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} className="w-full">
      <Tooltip
        label={
          contactData ? (
            <LabelTooltip contactData={contactData} removedAt={removedAt} />
          ) : (
            <LabelTooltipSkeleton />
          )
        }
        mode="hover"
        hoverOpenDelay={200}
        stayOnHover={true}
        className="w-full max-w-fit truncate"
        wrapperClassName={'w-full'}
      >
        {!contactId ? (
          children
        ) : (
          <LinkWithoutPrefetch
            className={'hover:text-text-tertiary/70 truncate underline'}
            href={
              removedAt
                ? `/contacts/bin?order=prev&filter=%5B%7B%22searchValue%22%3A%22${removedAtDateString}00%3A00%3A00%22%2C%22compareOperation%22%3A%22EQUALS%22%2C%22searchField%22%3A%22removed_at%22%7D%5D`
                : `/contacts/${contactId}`
            }
          >
            {children}
          </LinkWithoutPrefetch>
        )}
      </Tooltip>
    </div>
  )
}

export default LazyContactInfoTooltip
